import { useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ImageIcon,
  Upload,
  Search,
  Trash2,
  Copy,
  ExternalLink,
} from 'lucide-react';

import { useMediaListQuery } from '@/client/api/cms/media.query';
import {
  useDeleteMediaMutation,
  useUploadMediaMutation,
} from '@/client/api/cms/media.mutation';
import { CMSAsset, MediaListFilters } from '@/client/types/cms';

import Pagination from '@/client/components/common/Pagination';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useListFilters } from '@/client/hooks/useListFilters';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const MIME_OPTIONS: { value: MediaListFilters['mime']; label: string }[] = [
  { value: 'all',   label: 'All media' },
  { value: 'image', label: 'Images'    },
  { value: 'video', label: 'Videos'    },
];

const MediaLibraryPage = () => {
  useBreadcrumbs([
    { label: 'Content Management', href: '#' },
    { label: 'Media Library' },
  ]);

  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const {
    page,
    limit,
    filters,
    searchTerm,
    debouncedSearchTerm,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    handleSearch,
  } = useListFilters<{ mime: MediaListFilters['mime'] }>({
    initialFilters: { mime: 'all' },
  });

  const { data, isLoading } = useMediaListQuery(page, {
    mime: filters.mime,
    ...(debouncedSearchTerm ? { q: debouncedSearchTerm } : {}),
  });

  const uploadMutation = useUploadMediaMutation();
  const deleteMutation = useDeleteMediaMutation();

  const assets: CMSAsset[] = data?.assets ?? [];

  const upload = async (files: FileList | File[]) => {
    if (!files || (files instanceof FileList && files.length === 0)) return;
    const fileArr = Array.from(files);
    for (const file of fileArr) {
      try {
        await uploadMutation.mutateAsync(file);
      } catch (err: any) {
        toast.error(err?.message ?? `Failed to upload ${file.name}`);
        return;
      }
    }
    toast.success(`Uploaded ${fileArr.length} file(s)`);
  };

  const handleDelete = async (a: CMSAsset) => {
    if (!window.confirm(`Delete "${a.filename ?? a.asid}"?`)) return;
    try {
      await deleteMutation.mutateAsync(String(a.asid));
      toast.success('Deleted');
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to delete');
    }
  };

  const handleCopyUrl = async (a: CMSAsset) => {
    if (!a.public_url) {
      toast.error('No public URL');
      return;
    }
    try {
      await navigator.clipboard.writeText(a.public_url);
      toast.success('URL copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <ImageIcon className="h-7 w-7 text-blue-700" />
              <h1 className="text-2xl font-bold text-gray-900">Media Library</h1>
            </div>
            <p className="text-sm text-gray-500 ml-10">
              Images and videos available for use in CMS pages.
            </p>
          </div>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            className="hidden"
            onChange={e => upload(e.target.files ?? new FileList())}
          />
          <Button
            onClick={() => inputRef.current?.click()}
            disabled={uploadMutation.isPending}
            className="gap-2"
          >
            <Upload className="h-4 w-4" />
            {uploadMutation.isPending ? 'Uploading…' : 'Upload'}
          </Button>
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-lg shadow border p-4 mb-4 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              className="pl-9"
              placeholder="Search by filename or alt text…"
              value={searchTerm}
              onChange={e => handleSearch(e.target.value)}
            />
          </div>
          <div className="ml-auto flex items-center gap-2">
            {MIME_OPTIONS.map(o => (
              <button
                key={o.value}
                onClick={() => handleFilterChange('mime', o.value)}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                  filters.mime === o.value
                    ? 'bg-blue-100 text-blue-700 border border-blue-200'
                    : 'text-gray-600 hover:bg-gray-100 border border-transparent'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {/* Dropzone wrapping the grid */}
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => {
            e.preventDefault();
            setDragOver(false);
            if (e.dataTransfer.files) upload(e.dataTransfer.files);
          }}
          className={`bg-white rounded-lg shadow p-5 border-2 transition-colors ${
            dragOver ? 'border-blue-400 bg-blue-50/30' : 'border-transparent'
          }`}
        >
          {isLoading && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {Array.from({ length: 12 }).map((_, i) => (
                <Skeleton key={i} className="aspect-square w-full rounded-md" />
              ))}
            </div>
          )}

          {!isLoading && assets.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <ImageIcon className="h-12 w-12 text-gray-300 mb-3" />
              <p className="text-base font-medium text-gray-700">No media yet</p>
              <p className="text-sm text-gray-500 mb-4">Drag files here or click upload to get started.</p>
              <Button onClick={() => inputRef.current?.click()} className="gap-2">
                <Upload className="h-4 w-4" />
                Upload media
              </Button>
            </div>
          )}

          {!isLoading && assets.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {assets.map(asset => (
                <div
                  key={asset.asid}
                  className="group relative aspect-square overflow-hidden rounded-md border border-gray-200 bg-gray-50"
                >
                  {asset.mime_type?.startsWith('image/') ? (
                    <img
                      src={asset.public_url}
                      alt={asset.alt_text ?? asset.filename ?? ''}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-gray-500 p-2 text-center">
                      {asset.mime_type}
                    </div>
                  )}

                  {/* Hover overlay actions */}
                  <div className="absolute inset-0 flex flex-col justify-between p-2 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => handleCopyUrl(asset)}
                        title="Copy URL"
                        className="rounded bg-white/90 p-1.5 hover:bg-white"
                      >
                        <Copy className="h-3.5 w-3.5 text-gray-700" />
                      </button>
                      <a
                        href={asset.public_url}
                        target="_blank"
                        rel="noreferrer"
                        title="Open original"
                        className="rounded bg-white/90 p-1.5 hover:bg-white"
                      >
                        <ExternalLink className="h-3.5 w-3.5 text-gray-700" />
                      </a>
                      <button
                        onClick={() => handleDelete(asset)}
                        title="Delete"
                        className="rounded bg-white/90 p-1.5 hover:bg-white"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-red-600" />
                      </button>
                    </div>
                    <div className="text-[10px] text-white truncate">
                      {asset.filename ?? asset.asid}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 bg-white rounded-lg shadow px-4 py-3 sm:px-6">
          <Pagination
            total={data?.total_assets ?? 0}
            page={page}
            itemsPerPage={limit}
            limit={limit}
            setPage={handlePageChange}
            setItemsPerPage={handleLimitChange}
            showButtons
            showSelect
            selectOptions={[12, 24, 48, 96]}
          />
        </div>
      </div>
    </div>
  );
};

export default MediaLibraryPage;
